import * as React from 'react';
import { Button, Col, DropdownButton, MenuItem } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useCsvExport } from './useCsvExport';

export interface ExportRequest {
    /** API export endpoint. */
    url: string;
    /** Same filter payload the list/search endpoint receives. */
    body: unknown;
    /** Used only if the server omits a Content-Disposition filename. */
    filename: string;
}

/** Mirrors the server ExportFormat enum (Csv = 0, Excel = 1). */
const ExportFormat = { Csv: 0, Excel: 1 };

interface Iprops {
    /**
     * Builds the export request at click time so it always reflects the
     * filters/slicers the user currently has applied.
     */
    getExportRequest: () => ExportRequest;
    label?: string;
    disabled?: boolean;
    /**
     * 'button' (default) renders the prominent dropdown button. 'menuItems'
     * renders just two <MenuItem>s ("Export to CSV" / "Export to Excel") for
     * embedding inside an existing react-bootstrap dropdown menu (e.g. the
     * Inventory actions menu) - no button/Col wrapper, no busy spinner.
     */
    renderMode?: 'button' | 'menuItems';
    /** react-bootstrap bsStyle; defaults to a solid green "success" button. */
    bsStyle?: string;
    /** Wrap the button in a Bootstrap Col (default true, for SearchTableForm). */
    wrapInCol?: boolean;
    /**
     * Inline placement: sits at the end of the SearchTableForm filter row
     * (aligned with the inputs), matching the "New Job" / "New Facility"
     * convention. Default (false) puts it on its own full-width row.
     */
    inline?: boolean;
    colXs?: number;
    colLg?: number;
    className?: string;
}

/**
 * Shared "Export" control. Idle: a solid, prominent dropdown with two
 * options - "to CSV" and "to Excel". The chosen format is passed through as
 * an extra export setting; the server's data query is identical for both,
 * only the file writer differs. While running it collapses to the same
 * disabled button with a spinner, then auto-downloads the file. Reused by
 * every admin list view.
 */
export const ExportToExcelButton: React.FC<Iprops> = (props: Iprops) => {
    const { isExporting, runExport } = useCsvExport();

    const runWithFormat = (format: number): void => {
        const req = props.getExportRequest();
        const prevBody =
            req.body && typeof req.body === 'object'
                ? (req.body as { [key: string]: unknown })
                : {};
        const prevSettings =
            prevBody.settings && typeof prevBody.settings === 'object'
                ? (prevBody.settings as { [key: string]: unknown })
                : {};
        const body = {
            ...prevBody,
            settings: { ...prevSettings, format }
        };
        const filename =
            format === ExportFormat.Excel
                ? req.filename.replace(/\.csv$/i, '.xlsx')
                : req.filename;
        void runExport(req.url, body, filename);
    };

    // Embedded mode: two plain menu items for an existing dropdown menu.
    if (props.renderMode === 'menuItems') {
        return (
            <React.Fragment>
                <MenuItem
                    className="filter-dropdown-item"
                    disabled={isExporting || props.disabled}
                    onClick={() => runWithFormat(ExportFormat.Csv)}
                >
                    <FontAwesomeIcon icon={['far', 'file-csv']} fixedWidth />
                    <span className="filter-option-text">Export to CSV</span>
                </MenuItem>
                <MenuItem
                    className="filter-dropdown-item"
                    disabled={isExporting || props.disabled}
                    onClick={() => runWithFormat(ExportFormat.Excel)}
                >
                    <FontAwesomeIcon icon={['far', 'file-excel']} fixedWidth />
                    <span className="filter-option-text">Export to Excel</span>
                </MenuItem>
            </React.Fragment>
        );
    }

    const label = props.label || 'Export';
    const bsStyle = props.bsStyle || 'success';
    const className = `export-to-excel-button ${props.className || ''}`;

    // While running, reuse the existing disabled spinner button.
    const busyButton = (
        <Button bsStyle={bsStyle} bsSize="sm" className={className} disabled>
            <FontAwesomeIcon icon={['far', 'spinner']} spin size="lg" />
            <span className="export-to-excel-label">{label}</span>
        </Button>
    );

    const dropdownTitle = (
        <span>
            <FontAwesomeIcon icon={['far', 'file-export']} size="lg" />
            <span className="export-to-excel-label">{label}</span>
        </span>
    );

    const control = isExporting ? (
        busyButton
    ) : (
        <DropdownButton
            id="export-to-excel-dropdown"
            bsStyle={bsStyle}
            bsSize="sm"
            className={className}
            disabled={props.disabled}
            title={dropdownTitle}
            onSelect={(eventKey: any) =>
                runWithFormat(
                    eventKey === 'excel' ? ExportFormat.Excel : ExportFormat.Csv
                )
            }
        >
            <MenuItem eventKey="csv">
                <FontAwesomeIcon
                    icon={['far', 'file-csv']}
                    fixedWidth
                    style={{ marginRight: 8 }}
                />
                to CSV
            </MenuItem>
            <MenuItem eventKey="excel">
                <FontAwesomeIcon
                    icon={['far', 'file-excel']}
                    fixedWidth
                    style={{ marginRight: 8 }}
                />
                to Excel
            </MenuItem>
        </DropdownButton>
    );

    if (props.wrapInCol === false) {
        return control;
    }

    const inline = props.inline === true;

    return (
        <Col
            xs={props.colXs || (inline ? 3 : 12)}
            lg={props.colLg || (inline ? 2 : 12)}
            className={
                inline
                    ? 'export-to-excel-col export-to-excel-col--inline'
                    : 'export-to-excel-col'
            }
        >
            {control}
        </Col>
    );
};

export default ExportToExcelButton;
